import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, provideIntlService, registerForLocalization, registerForIntl } from '@progress/kendo-react-intl';
import { Field } from '@progress/kendo-react-form';
import { FormPage, TextBox, TextArea, DatePicker, NumericBox, TimePicker } from '../../../ui';
import { Hint } from '@progress/kendo-react-labels';
import { DatePeriodFormatter } from '../../shared';
import {
  loadSessionFormPage,
  saveSession,
  cancelSessionFormPage
} from '../actions';
import { withRouter } from '../../../components/withRouter';

class GeneralCompetencyEventSessionFormPage extends React.PureComponent {

  constructor() {
    super();
    this.handleValidate = this.handleValidate.bind(this);
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
  }

  async componentDidMount() {
    await this.props.loadSessionFormPage({
      eventId: this.props.params.generalCompetencyEventId,
      sessionId: this.props.params.sessionId
    });
  }

  componentWillUnmount() {
    this.props.cancelSessionFormPage();
  }

  render() {
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    return (
      <FormPage
        title={this.props.eventName
          ? `${localizationService.toLanguageString('generalCompetencyEventSession.formTitle')} (${this.props.eventName})`
          : localizationService.toLanguageString('generalCompetencyEventSession.formTitle')}
        breadcrumbItems={[
          {
            id: 'general_competency_events',
            text: localizationService.toLanguageString('generalCompetencyEvent.listTitle')
          },
          {
            id: 'general_competency_event',
            text: localizationService.toLanguageString('generalCompetencyEvent.overviewTitle')
          },
          {
            id: 'session',
            text: localizationService.toLanguageString('generalCompetencyEventSession.formTitle'),
            disabled: true
          }
        ]}
        onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
        onBackClick={this.handleBackClick}
        renderBackButton={true}
        validationResult={this.props.validationResult}
        loading={this.props.loading}
        initialValues={this.props.session}
        localizationService={localizationService}
        onSubmit={this.handleSubmit}
        onCancel={this.handleCancel}
        validator={this.handleValidate}
        render={() => (
          <>
            <Field
              name='name'
              label={localizationService.toLanguageString('generalCompetencyEventSession.name')}
              component={TextBox}
              width='320px'
            />
            <Field
              name='description'
              label={localizationService.toLanguageString('generalCompetencyEventSession.description')}
              id='description'
              component={TextArea}
              autoSize={true}
              width='320px'
            />
            <Field
              name='lecturerName'
              label={localizationService.toLanguageString('generalCompetencyEventSession.lecturerName')}
              component={TextBox}
              width='320px'
            />
            <Field
              name='startAtDate'
              label={localizationService.toLanguageString('generalCompetencyEventSession.startAtDate')}
              component={DatePicker}
              width='320px'
            />
            {this.props.eventStartDate && this.props.eventEndDate && (
              <Hint>
                {localizationService.toLanguageString('generalCompetencyEventSession.eventDateRange')
                  .replace('{0}', DatePeriodFormatter(this.props.eventStartDate, this.props.eventEndDate, intlService))}
              </Hint>
            )}
            <Field
              name='startAtTime'
              label={localizationService.toLanguageString('generalCompetencyEventSession.startAtTime')}
              component={TimePicker}
              width='320px'
            />
            <Field
              name='durationMinutes'
              label={localizationService.toLanguageString('generalCompetencyEventSession.durationMinutes')}
              component={NumericBox}
              min={1}
              width='320px'
            />
            <Field
              name='location'
              label={localizationService.toLanguageString('generalCompetencyEventSession.location')}
              component={TextBox}
              width='320px'
            />
          </>
        )}
      />
    );
  }

  handleValidate(values, localizationService, validationResult, modified) {
    const errors = {};
    const maxNameLength = 500;
    const maxDescriptionLength = 5000;
    const maxLecturerNameLength = 500;
    const maxLocationLength = 500;
    if (!values.name) {
      errors.name = localizationService.toLanguageString('validation.required');
    } else if (values.name.length > maxNameLength) {
      errors.name = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxNameLength})`;
    }
    if (values.description && values.description.length > maxDescriptionLength) {
      errors.description = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxDescriptionLength})`;
    }
    if (values.lecturerName && values.lecturerName.length > maxLecturerNameLength) {
      errors.lecturerName = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxLecturerNameLength})`;
    }
    if (values.location && values.location.length > maxLocationLength) {
      errors.location = `${localizationService.toLanguageString('validation.exceedsMaximumLength')}(${maxLocationLength})`;
    }
    if (!values.startAtDate) {
      errors.startAtDate = localizationService.toLanguageString('validation.required');
    }
    if (!values.startAtTime) {
      errors.startAtTime = localizationService.toLanguageString('validation.required');
    }
    if (!values.durationMinutes || values.durationMinutes <= 0) {
      errors.durationMinutes = localizationService.toLanguageString('validation.required');
    }
    if (values.startAtDate && values.durationMinutes > 0 && this.props.eventStartDate && this.props.eventEndDate) {
      const sessionStart = new Date(values.startAtDate);
      if (values.startAtTime) {
        const time = new Date(values.startAtTime);
        sessionStart.setHours(time.getHours(), time.getMinutes(), 0, 0);
      }
      const sessionEnd = new Date(sessionStart.getTime() + values.durationMinutes * 60000);
      const eventStart = new Date(this.props.eventStartDate);
      eventStart.setHours(0, 0, 0, 0);
      const eventEnd = new Date(this.props.eventEndDate);
      eventEnd.setHours(0, 0, 0, 0);
      eventEnd.setDate(eventEnd.getDate() + 1);
      if (sessionStart < eventStart || sessionEnd > eventEnd) {
        errors.startAtDate = localizationService.toLanguageString('generalCompetencyEventSession.sessionOutsideEventDateRange');
      }
    }
    if (!modified.name) {
      if (validationResult?.errors?.name) {
        errors.name = validationResult?.errors?.name;
      }
    }
    return errors;
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'general_competency_events') {
      this.props.navigate('/general_competency_events');
    }
    if (e.id === 'general_competency_event') {
      this.props.navigate(`/general_competency_events/${this.props.params.generalCompetencyEventId}/overview`);
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleCancel() {
    this.props.navigate(-1);
  }

  handleSubmit(payload) {
    const { startAtDate, startAtTime, ...rest } = payload;
    let startAt = null;
    if (startAtDate) {
      const date = new Date(startAtDate);
      if (startAtTime) {
        const time = new Date(startAtTime);
        date.setHours(time.getHours(), time.getMinutes(), 0, 0);
      }
      startAt = date;
    }
    this.props.saveSession({
      ...rest,
      startAt,
      eventId: this.props.params.generalCompetencyEventId
    });
  }
}

registerForLocalization(GeneralCompetencyEventSessionFormPage);
registerForIntl(GeneralCompetencyEventSessionFormPage);

const mapStateToProps = state => ({
  loading: state.generalCompetencyEventSessionFormPage.loading,
  session: state.generalCompetencyEventSessionFormPage.session,
  validationResult: state.generalCompetencyEventSessionFormPage.validationResult,
  eventName: state.generalCompetencyEventSessionFormPage.eventName,
  eventStartDate: state.generalCompetencyEventSessionFormPage.eventStartDate,
  eventEndDate: state.generalCompetencyEventSessionFormPage.eventEndDate
});

const mapDispatchToProps = dispatch => ({
  loadSessionFormPage: (payload) => dispatch(loadSessionFormPage(payload)),
  saveSession: (payload) => dispatch(saveSession(payload)),
  cancelSessionFormPage: () => dispatch(cancelSessionFormPage())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(GeneralCompetencyEventSessionFormPage));
